import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {Store} from '@ngrx/store';
import * as _ from 'lodash';
import {combineLatest, iif, Observable, of, OperatorFunction, pipe} from 'rxjs';
import {flatMap, map, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {Aggregate} from '../../../models/aggregate';
import {
  AggregateOptions,
  AggregateParamsBuilder,
  AggregateRequestParams
} from '../../../models/aggregate-request-params';
import {CdxDocument} from '../../../models/cdx-document';
import {CdxTask} from '../../../models/cdx-task';
import {DraftDocument} from '../../../models/DraftDocument';
import {Entity} from '../../../models/Entity';
import {EsPage} from '../../../models/es-page';
import {SearchOperator} from '../../../models/field';
import {InternalRoutes} from '../../../models/internal-routes';
import {ObjectType} from '../../../models/ObjectType';
import {SearchItem} from '../../../models/search-item';
import {SearchPath} from '../../../models/search-path';
import {SearchResultPagination} from '../../../models/search-result-pagination';
import {SearchResultPaginationSort} from '../../../models/search-result-pagination-sort';
import {ThesaurusService} from '../../../services/thesaurus/thesaurus.service';
import {selectCurrentContextDatasCurrentDomain} from '../../current-context/current-context-selectors';
import {FilterDatas} from '../../document-search/document-filter/reducer/filter.reducer';
import {EntityFilterDatas} from '../../entity-search/entity-filter/reducer/entity-filter.reducer';
import {selectGedCfgFieldFeature} from '../../field/field-selectors';
import {AbstractCfgFieldService} from '../../field/service/abstract-cfg-field.service';
import {DraftFilterDatas} from '../../indexation/draft-filter/reducer/draft-filter.reducer';
import {selectSearchResultPaginationDatas} from '../../search-result-pagination/search-result-pagination-selectors';
import {TaskFilterDatas} from '../../task-search/task-filter/reducer/task-filter.reducer';
import { CustomURLEncoder } from '../../../utils/CustomURLEncoder';
import { AuthenticationService } from '../../../../entry-routes/authentication/authentication.service';

export enum SearchOperatorParam {
  NOT_EQ = 'ne',
  EQ = 'eq'
}

@Injectable({
  providedIn: 'root'
})
export abstract class AbstractSearchResultService {

  public static readonly AGGREGATE_DEFAULT_SIZE = 5;
  public static readonly AGGREGATE_MAX_SIZE = 50;
  public static readonly AGGREGATE_SIZE_STEP = 5;
  public static readonly DOCUMENT_PAGE_SIZE = 24;
  public static readonly TASK_PAGE_SIZE = 10;
  public static readonly ENTITY_PAGE_SIZE = 24;
  public static readonly DOCUMENT_MAX_SIZE = 200;
  public static readonly AGGS_DELIMITTER = '|';
  public static readonly DESC = 'desc';
  public static readonly ASC = 'asc';

  currentDomain$ = this.store.select(selectCurrentContextDatasCurrentDomain);
  gedCfgFields$ = this.store.select(selectGedCfgFieldFeature);
  searchResultPaginationStore$ = this.store.select(selectSearchResultPaginationDatas);
  private pageSize;
  private searchResultPaginationStore: { [searchTypeKey: string]: SearchResultPagination };
  protected sortKey: string;
  protected sortDirection: string;

  constructor(
    protected httpClient: HttpClient,
    protected cfgFieldService: AbstractCfgFieldService,
    protected thesaurusService: ThesaurusService,
    protected store: Store,
    protected authenticationService: AuthenticationService
  ) {
    this.subscribeToSearchResultpaginationStore();
  }

  /**
   * Check if at least one param is search type
   * @param params
   *
   * return true if key are keyword - value (cdx_datas.numfact=33) or query param (_q=Paris)
   */
  private static hasRequestItem(params: HttpParams): boolean {
    const keys = params.keys();
    for (let index = 0; index < keys.length; index++) {
      if (keys[index] === '_q' || keys[index].indexOf('_') !== 0) {
        return true;
      }
    }
    return false;
  }

  public abstract search(): void;

  public abstract filter(): void;

  public abstract loadAggregate(path: string, opts: AggregateOptions, checkPath?: boolean): void;

  public abstract searchDrafts(pageNumber, keepLastAgg): void;

  public abstract setNewPageToStore(page: EsPage<CdxDocument | Entity | CdxTask>);

  public loadMoreAggregate(agg: Aggregate): void {
    const opts = {
      size: this.getSizeFromAggregate(agg)
    } as AggregateOptions;

    this.loadAggregate(agg.path, opts, true);
  }

  log<T>(message?: string): OperatorFunction<T, T> {
    return tap(e => console.info('AbstractSearchResultService: ', message, e));
  }

  public workspaceParams(): OperatorFunction<HttpParams, HttpParams> {
    return pipe(
      withLatestFrom(this.currentDomain$),
      map(([params, currentDomainCode]) => {
        if (!!currentDomainCode) {
          params = params.append('_domains', currentDomainCode);
        }
        return params;
      })
    );
  }

  public searchItemsParams(pathToCheck = null): OperatorFunction<HttpParams, HttpParams> {
    let searchPathToCheck: string;
    if (pathToCheck) {
      searchPathToCheck = new SearchPath(pathToCheck).getPathWithoutSuffix();
    }
    return pipe(
      withLatestFrom(this.getSearchItem$()),
      map(([params, searchItems]) => {
        if (!!searchItems) {
          const _q: SearchItem[] = [];
          searchItems.forEach((searchItem: SearchItem) => {
            if (!searchItem.path) {
              _q.push(searchItem);
            } else {
              const path = searchItem.path;
              if(!searchItem.isMetaDataAlert()) {
                const searchPath = searchItem.path ? new SearchPath(searchItem.path) : undefined;
                if (searchItem.value) {
                  if (searchPath?.getPathWithoutSuffix() === searchPathToCheck) {
                    params = params.append(path + ':' + SearchOperatorParam.NOT_EQ, searchItem.value);
                  } else if (searchItem.operator) {
                    params = params.append(path + ':' + searchItem.operator, searchItem.value);
                  } else {
                    params = params.append(path, searchItem.value);
                  }
                }
              } else {
                params = params.append(path + ':' + SearchOperator.lt, 'now' );
              }
            }
          });
          if (_q.length > 0) {
            _q.forEach((searchItem: SearchItem) => {
              params = params.append('_q', searchItem.operator ? searchItem.operator + ':' + searchItem.value : searchItem.value);
            });
          }
        }
        return params;
      })
    );
  }

  public filterItemsParams(path = null): OperatorFunction<HttpParams, HttpParams> {
    return pipe(
      // withLatestFrom(this.filterItems$),
      withLatestFrom(this.getFilterDatas$()),
      map(([params, filterDatas]) => {
        if (!!filterDatas && !!filterDatas.filterItems && filterDatas.filterItems.length > 0) {
          filterDatas.filterItems.forEach((filterItem: SearchItem) => {
            if (!filterItem.operator) {
              if (!path || (!!path && path !== filterItem.path)) {
                params = params.append(filterItem.path, filterItem.value);
              }
            } else {
              if (!path || (!!path && path !== filterItem.path)) {
                params = params.append(filterItem.path + ':' + filterItem.operator, filterItem.value);
              }
            }
          });
        }
        return params;
      })
    );
  }

  public coordParams(limit: number = AbstractSearchResultService.DOCUMENT_PAGE_SIZE, offset = 0): OperatorFunction<HttpParams, HttpParams> {
    return map(params => this.addPageCoord(params, limit, offset));
  }

  public changePageSize(pageSize: number): void {
    this.pageSize = pageSize;
    this.updateContent();
  }

  public changeSort(sort: SearchResultPaginationSort): void {
    this.sortKey = sort.sortKey;
    this.sortDirection = sort.sortDirection;
    this.updateContent();
  }

  protected abstract getServiceType(): InternalRoutes;

  protected abstract getObjectType(): ObjectType;

  protected abstract getSearchItem$(): Observable<SearchItem[]>;

  protected abstract getFilterDatas$(): Observable<FilterDatas | EntityFilterDatas | DraftFilterDatas | TaskFilterDatas>;

  protected abstract getSearchListResult$(): Observable<EsPage<CdxDocument | Entity | DraftDocument | CdxTask>>;

  protected abstract getEmptyEspage(): EsPage<CdxDocument | Entity | DraftDocument | CdxTask>;

  protected abstract getSearchBaseUrl(): string;

  protected abstract getAggBaseUrl(): string;

  protected _search(): Observable<EsPage<CdxDocument | Entity | DraftDocument | CdxTask>> {
    return this.query()
      .pipe(
        this.workspaceParams(),
        this.searchItemsParams(),
        this.filterItemsParams(),
        this.sortByKeyAndDirection(),
        this.page(0),
        this.request()
      );
  }

  protected _getDocumentsByEntityId(entityTypeCode: string, entityId: string): Observable<EsPage<CdxDocument>> {
    const path: string = SearchPath.PREFIX_CDX_LINKS + '.' + entityTypeCode + '.' + SearchPath.META_CDX_ID;
    return this.query()
      .pipe(
        this.workspaceParams(),
        this.addKeyValueParams(path, entityId),
        this.sort(SearchPath.META_CDX_CREATION_DATE, AbstractSearchResultService.DESC),
        this.page(),
        this.noAggregateParams(),
        // this.log(),
        this.request(),
        map((response) => response as EsPage<CdxDocument>)
      );
  }

  protected _filter(): Observable<EsPage<CdxDocument | Entity | CdxTask>> {
    return this.query()
      .pipe(
        this.workspaceParams(),
        this.searchItemsParams(),
        this.filterItemsParams(),
        this.sortByKeyAndDirection(),
        this.page(0),
        this.currentAggregateParams(),
        // this.log(),
        this.request()
      );
  }

  protected _gotoPage(pageNum: number): Observable<EsPage<CdxDocument | Entity | CdxTask>> {
    return this.query()
      .pipe(
        this.workspaceParams(),
        this.searchItemsParams(),
        this.filterItemsParams(),
        this.sortByKeyAndDirection(),
        this.page(pageNum),
        this.noAggregateParams(),
        // this.log(),
        this.request()
      );
  }

  protected _loadAggregate(path: string, opts: AggregateOptions, checkPath = false): Observable<Aggregate> {
    return this.query().pipe(
      this.workspaceParams(),
      this.searchItemsParams(),
      checkPath ? this.filterItemsParams(path) : this.filterItemsParams(),
      this.requestAggregate(path, opts)
    );
  }

  protected getSizeFromAggregate(agg: Aggregate): number {
    let size = agg.buckets.length;
    size = size - (size % AbstractSearchResultService.AGGREGATE_SIZE_STEP) + AbstractSearchResultService.AGGREGATE_SIZE_STEP;
    return size;
  }

  protected buildAggregateParams(path: string, options: AggregateOptions): Observable<AggregateRequestParams> {

    const searchpath: SearchPath = new SearchPath(path);
    const aggregateParamsBuilder = new AggregateParamsBuilder()
      .path(searchpath)
      .options(options);

    const aggregateParamsObservable: Observable<AggregateRequestParams> =
      iif(() => searchpath.isMeta,
        of(aggregateParamsBuilder),
        this.cfgFieldService.loadFields(searchpath.code)
          .pipe(
            map(fields => {
              aggregateParamsBuilder.fieldType(fields[searchpath.code].type);
              return aggregateParamsBuilder;
            })
          ))
        .pipe(
          map(builder => builder.build())
        );
    return aggregateParamsObservable;
  }

  protected requestAggregate(path: string, opts: AggregateOptions): OperatorFunction<HttpParams, Aggregate> {
    return switchMap(params => {
      return this.buildAggregateParams(path, opts)
        .pipe(
          flatMap(aggReqParams => {
            return this._requestAggregate(aggReqParams, params);})
        );

    });
  }

  protected currentAggregateParams(): OperatorFunction<HttpParams, HttpParams> {
    const currentUserId = this.authenticationService.getUserCredentials().id;
    return pipe(
      withLatestFrom(this.getSearchListResult$()),
      map(([params, page/*, gedFields*/]) => {
        const clonePage: EsPage<CdxDocument | Entity | DraftDocument | CdxTask> = _.cloneDeep(page); // in order to not change the path of the aggs by ref
        const cloneParams: HttpParams = _.cloneDeep(params); // in order to not duplicate our params

        /*Redmine 29675: Ajout d'un nouveau filtre : Assignation (modification de requête et des agg pour le filtre assignation*/
        // let newParams = new HttpParams();
        // params.keys().forEach(key => {
        //   const value = params.get(key);
        //   if (key === 'cdx_wkf.assignation') {
        //     newParams = newParams.set(`${key}.${value}`, currentUserId);
        //   } else {
        //     newParams = newParams.set(key, value!);
        //   }
        // });


        if (!!page && !!page.aggs) {
          const aggRequestParams$: Observable<AggregateRequestParams>[] = [];
          clonePage.aggs.forEach((agg: any) => {
            const min = cloneParams.get(agg.path + SearchItem.OPERATOR_SEPARATOR + SearchOperator.gte);
            const max = cloneParams.get(agg.path + SearchItem.OPERATOR_SEPARATOR + SearchOperator.lte);
            const itemPath: string[] = agg.itemPath;
            const aggregateOptions: AggregateOptions = new AggregateOptions();

             /*Redmine 29675: Ajout d'un nouveau filtre : Assignation (modification de requête et des agg pour le filtre assignation*/
            if (agg.path === 'cdx_wkf.assignation'){
              aggregateOptions.size = currentUserId;
            }else{
              aggregateOptions.size = AbstractSearchResultService.AGGREGATE_DEFAULT_SIZE;
            }
            if (!!min && !!max) {
              aggregateOptions.min = min;
              aggregateOptions.max = max;
            }
            if (!!itemPath) {
              aggregateOptions.itempath = itemPath;
            }
            const aggregateRequestParams$:Observable<AggregateRequestParams> = this.buildAggregateParams(agg.path, aggregateOptions);
            aggRequestParams$.push(aggregateRequestParams$);

          });
          return [params, combineLatest(aggRequestParams$)];
        }
        return [params, of([])];
      }),
      switchMap(([params, aggRequestParams$]: [HttpParams, Observable<AggregateRequestParams[]>]) => {
        return aggRequestParams$.pipe(
          map((aggRequestParams: AggregateRequestParams[]) => {
            const aggs: string[] = [];
            aggRequestParams.forEach((aggRequestParam: AggregateRequestParams) => {
              aggs.push(aggRequestParam.getAggregatePath());
            });
            if (aggs.length > 0) {
              params = params.set('_aggs', aggs.join(AbstractSearchResultService.AGGS_DELIMITTER));
            }
            return params;
          })
        );
      })
    );
  }

  protected addPageCoord(params: HttpParams, limit: number = AbstractSearchResultService.DOCUMENT_PAGE_SIZE, offset = 0) {
    return params
      .set('_limit', '' + Math.min(limit, AbstractSearchResultService.DOCUMENT_MAX_SIZE))
      .set('_offset', '' + offset);
  }

  protected page(pageNumber = 0): OperatorFunction<HttpParams, HttpParams> {
    return map((params) => {
      const limit = this.getPageSize();
      const offset = limit * Math.floor(pageNumber);
      return this.addPageCoord(params, limit, offset);
    });
  }

  protected query(): Observable<HttpParams> {
    const httpParams = new HttpParams({encoder: new CustomURLEncoder() });
    return of(httpParams);
  }

  protected sort(sort: any, order: any): OperatorFunction<HttpParams, HttpParams> {
    return map((params) => {
      if (!!sort) {
        if (!!order) {
          sort = sort + ':' + order;
        }
        return params.append('_sort', sort);
      }
    });
  }

  protected sortByKeyAndDirection(): OperatorFunction<HttpParams, HttpParams> {
    if (!!this.searchResultPaginationStore) {
      const objectType: ObjectType = this.getObjectType();
      this.sortKey = this.searchResultPaginationStore[objectType].sortKey;
      this.sortDirection = this.searchResultPaginationStore[objectType].sortDirection;
    }
    return map((params) => {
      if (!!this.sortKey) {

        let sort = '';
        let order = AbstractSearchResultService.DESC;
        if (!!this.sortDirection) {
          order = this.sortDirection;
        }
        sort = this.sortKey + ':' + order;

        params = params.append('_sort', sort);
      }

      return params;
    });
  }

  private subscribeToSearchResultpaginationStore(): void {
    const objectType: ObjectType = this.getObjectType();
    this.searchResultPaginationStore$.subscribe((searchResultPaginationStore) => {
      this.searchResultPaginationStore = searchResultPaginationStore;
      if (this.searchResultPaginationStore && this.searchResultPaginationStore[objectType] && this.searchResultPaginationStore[objectType].pageSize) {
        this.pageSize = this.searchResultPaginationStore[objectType].pageSize;
      } else {
        this.pageSize = AbstractSearchResultService.DOCUMENT_PAGE_SIZE;
      }
    });
  }

  protected request(): OperatorFunction<HttpParams, EsPage<CdxDocument | Entity | CdxTask>> {
    return switchMap(params => {
      if (AbstractSearchResultService.hasRequestItem(params) || this.getServiceType() === InternalRoutes.TASKS) {
        return this.httpClient.get<any>(this.getSearchBaseUrl(), {params: params});
      } else {
        return of(this.getEmptyEspage());
      }
    });
  }

  private _requestAggregate(aggReqParams: AggregateRequestParams, params: HttpParams): Observable<Aggregate> {
    switch (this.getServiceType()) {
      case InternalRoutes.TASKS:
      case InternalRoutes.DOCUMENTS:
      case InternalRoutes.ENTITIES:
      {
        if (AbstractSearchResultService.hasRequestItem(params) || this.getServiceType() === InternalRoutes.TASKS) {
          return this.httpClient.get<Aggregate>(this.getAggBaseUrl() + aggReqParams.getAggregatePath(), {params: params});
        } else {
          return of();
        }
      }
      case InternalRoutes.INDEXATION: {
        return this.httpClient.get<Aggregate>(this.getAggBaseUrl() + aggReqParams.getAggregatePath(), {params: params});
      }
    }
  }

  private noAggregateParams(): OperatorFunction<HttpParams, HttpParams> {
    return map(params => params.set('_aggs', ''));
  }

  private addKeyValueParams(path: string, value: string): OperatorFunction<HttpParams, HttpParams> {
    return map((params) => {
      return params.append(path, value);
    });
  }

  private getPageSize(): number {
    const objectType: ObjectType = this.getObjectType();
    if (objectType === ObjectType.DOCUMENT) {
      this.pageSize = AbstractSearchResultService.DOCUMENT_PAGE_SIZE;
    }
    else if (objectType === ObjectType.TASK) {
      this.pageSize = AbstractSearchResultService.TASK_PAGE_SIZE;
    }
    else if (objectType === ObjectType.ENTITY) {
      this.pageSize = AbstractSearchResultService.ENTITY_PAGE_SIZE;
    } else {
      this.pageSize = AbstractSearchResultService.DOCUMENT_PAGE_SIZE;
    }

    if (!!this.searchResultPaginationStore) {
      const objectType: ObjectType = this.getObjectType();
      this.pageSize = this.searchResultPaginationStore[objectType].pageSize;
    }
    return this.pageSize;
  }

  private updateContent(): void {
    const objectType: ObjectType = this.getObjectType();
    if (objectType !== ObjectType.DRAFT) {
      this.filter();
    } else {
      this.search();
    }
  }

}
